import { useTranslation } from "react-i18next"
import { Icon, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from "@nexus/ui"
import { formatMetricValue } from "@/features/dashboard/lib/format"
import { shareOfSpendPercent } from "../../lib/persona-format"
import { PERSONA_SPEND_FILL_DURATION_MS } from "../../constants/personas.constants"
import { useCountUp } from "@/lib/motion"
import { useTenantCurrency } from "@/lib/use-tenant-currency"

export interface PersonaKpiProps {
  /** Persona's tracked spend — numerator. */
  spend: number
  /** Tenant-wide total tracked spend across all (non-hidden) personas — denominator, never the filtered subset (B3). */
  totalSpend: number
  adCount: number
  /** Delays the meter's first-paint grow-in so a grid of cards reads as one wave (see `PersonaGrid`). */
  fillDelayMs?: number
  className?: string
}

/** Share-of-Spend KPI (B3) — shared between `PersonaCard` and `PersonaDetailDrawer` so the
 * card and drawer numbers can never drift apart. */
export function PersonaKpi({ spend, totalSpend, adCount, fillDelayMs = 0, className }: PersonaKpiProps) {
  const { t } = useTranslation()
  const { symbol: currencySymbol } = useTenantCurrency()
  const sharePct = shareOfSpendPercent(spend, totalSpend)
  // The readout is the meter's label, so it counts on the meter's clock rather than
  // snapping to the final figure while the bar is still growing behind it.
  const displayPct = useCountUp(Math.round(sharePct), {
    durationMs: PERSONA_SPEND_FILL_DURATION_MS,
    delayMs: fillDelayMs,
  })

  // Tinted surface, not a bordered card — this sits inside PersonaCard, and a border here
  // would read as a card nested in a card.
  return (
    <div className={cn("flex flex-col gap-1.5 rounded-md bg-muted/40 p-3", className)}>
      <div className="flex items-center justify-between text-xs">
        <span className="inline-flex items-center gap-1 font-medium text-muted-foreground">
          {t("personas.card.shareOfSpend", "Share of Spend")}
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-help">
                  <Icon name="info" size="xs" className="text-muted-foreground/50" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                {t(
                  "personas.card.shareOfSpendTooltip",
                  "Percentage of your total tracked spend attributed to this persona.",
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
        <span className="font-semibold tabular-nums text-foreground">{displayPct}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        {/* Scaled rather than sized so the grow-in and later share changes stay on the
            compositor — `persona-spend-fill` (globals.css) also drives the table's meter. */}
        <div
          className="persona-spend-fill h-full w-full rounded-full bg-primary transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
          style={{
            transform: `scaleX(${sharePct / 100})`,
            animationDelay: fillDelayMs > 0 ? `${fillDelayMs}ms` : undefined,
          }}
        />
      </div>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>
          {t("personas.card.spendOfTotal", "{{spend}}/mo of {{total}}/mo total", {
            spend: formatMetricValue(spend, "currency", currencySymbol),
            total: formatMetricValue(totalSpend, "currency", currencySymbol),
          })}
        </span>
        <span>{t("personas.card.adCount", "{{count}} ads", { count: adCount })}</span>
      </div>
    </div>
  )
}
